import { authenticatedFetch } from './api';

const FX_CACHE_KEY = 'fx_rates_eur_base_v1';
const FX_CACHE_TS_KEY = 'fx_rates_eur_base_ts_v1';
const FX_SOURCE_KEY = 'fx_rates_source_v1';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export type ExchangeRates = Record<string, number>;

function hasUsableRates(rates: ExchangeRates): boolean {
  return Boolean(rates.EUR === 1 && rates.INR && rates.INR > 10 && rates.USD && rates.USD > 0.5);
}

/** Rates: units of each currency per 1 EUR */
export async function fetchExchangeRates(): Promise<ExchangeRates> {
  const cached = readCachedRates();
  if (cached && hasUsableRates(cached)) {
    return cached;
  }

  if (!localStorage.getItem('accessToken')) {
    const stale = readStaleCachedRates();
    if (stale) return stale;
    throw new Error('Sign in to load live exchange rates');
  }

  const response = await authenticatedFetch('/api/v1/admin/exchange-rates');
  const body = await response.json().catch(() => ({}));

  if (!response.ok || !body?.success || !body?.data?.rates) {
    throw new Error(body?.error || 'Failed to fetch exchange rates');
  }

  const rates: ExchangeRates = { EUR: 1, ...body.data.rates };

  if (!hasUsableRates(rates)) {
    throw new Error('Exchange rates response is incomplete');
  }

  try {
    localStorage.setItem(FX_CACHE_KEY, JSON.stringify(rates));
    localStorage.setItem(FX_CACHE_TS_KEY, String(Date.now()));
    localStorage.setItem(FX_SOURCE_KEY, body.data.source || 'api');
  } catch {
    // ignore quota errors
  }

  return rates;
}

function parseStoredRates(raw: string | null): ExchangeRates | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ExchangeRates;
    const rates = parsed.EUR === 1 ? parsed : { EUR: 1, ...parsed };
    return hasUsableRates(rates) ? rates : null;
  } catch {
    return null;
  }
}

function readCachedRates(): ExchangeRates | null {
  try {
    const ts = Number(localStorage.getItem(FX_CACHE_TS_KEY) || 0);
    if (!ts || Date.now() - ts > CACHE_TTL_MS) return null;
    return parseStoredRates(localStorage.getItem(FX_CACHE_KEY));
  } catch {
    return null;
  }
}

function readStaleCachedRates(): ExchangeRates | null {
  return parseStoredRates(localStorage.getItem(FX_CACHE_KEY));
}

/** Convert amount between currencies using EUR-based rates. Returns null if rates missing. */
export function convertWithRates(
  amount: number,
  fromCode: string,
  toCode: string,
  rates: ExchangeRates,
): number | null {
  if (!Number.isFinite(amount) || amount === 0) return 0;
  if (fromCode === toCode) return amount;

  const fromRate = rates[fromCode];
  const toRate = rates[toCode];
  if (!fromRate || !toRate) return null;

  const amountInEur = fromCode === 'EUR' ? amount : amount / fromRate;
  return toCode === 'EUR' ? amountInEur : amountInEur * toRate;
}
